import moment from 'moment'

import exclamation from 'app/assets/icons/exclamation.png'
import greenTick from 'app/assets/icons/green-tick.png'
import jobIcon from 'app/assets/icons/summary_icons/job.svg'
import { Box, Flex } from 'app/components/design-system-components'
import * as Display from 'app/components/display/display'
import { ContainerHeader } from 'app/components/display/text/standard_text/standard-headings/component'
import ReferenceResultComponent from 'app/match/rental_reference/display/renter_address_history_detailed/rental_reference_detailed/rental_reference_result/rental_reference_result_item'
import { resendReferenceRequest } from 'app/services/http/employment'
import * as snugNotifier from 'app/services/snugNotifier'
import {
  calculateDuration,
  capitalizeFirstLetter,
  deCapitalizeFirstLetter,
  EmploymentDeclineReasons,
  EmploymentTypes,
  EmploymentVerifyStatus,
  getBusinessNumberLabel,
  isEmploymentDeclineContactPerson,
  isEmploymentDeclineEmploymentDate,
  isEmploymentDeclineOther,
  isEmploymentDeclineReasonCompanyName,
  isEmploymentDeclineReasonJobTitle,
  isEmploymentDeclineReasonSalary,
  isEmploymentStatusUnverified,
  isEmploymentStatusVerified,
  isSelfEmployed,
  isSelfEmployedNoAccountant,
  noteActionType,
  refereeTypeCollection,
  refereeTypeObject,
  urlTo,
} from 'app/sm/helpers'
import { ApplicationDetailsNumbersComponent } from 'app/sm/renter_applications/renter_application_detailed/components/new_applicant-details'
import { currencyFormat } from 'app/utils/numbers/helpers'
import * as phoneNumberHelpers from 'app/utils/phonenumber/helpers'
import * as stringHelpers from 'app/utils/strings/helpers'
import * as Features from 'config/features'

type Employer = {
  guidID: string
  employerName?: string
  employerTradingName?: string
  endDate: string
  grossAnnualIncome: number
  jobTitle: string
  phoneNumber: string
  contactName?: string
  email?: string
  employmentType?: number
  stillEmployed?: boolean
  startDate: string
  weeklyNetIncome: number
  employerABN?: string
  verifyDate?: string
  verifyStatus?: number
  address: {
    friendlyName: string
    suburb: string
    state: string
    postcode: string
    country: string
  }
  employerConfirmation?: {
    guidID?: string
    comment?: string
    reason?: number
    refereeType?: number
  }
  hasAccountant?: boolean
  lastConfirmationRequest?: {
    requestReceiver?: string
    requestSender?: string
    requestSentTime?: string
  }
  confirmedContactName?: string
  confirmedEmail?: string
  confirmedEmploymentType?: number
  confirmedEndDate?: string
  confirmedGrossAnnualIncome?: number
  confirmedJobTitle?: string
  confirmedPhoneNumber?: string
  confirmedStartDate?: string
  confirmedWeeklyNetIncome?: number
  confirmedStillEmployed?: boolean
  reasonAffectingEmployment?: string
  pendingEmployerConfirmationGuidID?: string
}

type Note = {
  targetGUID: string
  updatedAt: string // ISO date string
  type: number
}

type EmploymentComponentProps = {
  currentEmployers: any[]
  previousEmployers: any[]
  displayedApplicant: any
  onNewNoteUpdated: (note: string) => void
  newNotesContent: string
  onNoteIconClicked: (note: string) => void
  onNewNoteAdded: (note: string) => void
  onNoteCrossClicked: () => void
  interpretNoteResult?: any
  enableNotesButton?: boolean
  section?: string
  applicationId?: string
  id?: string
  toggleRequestInfoModal?: () => void
  agencyGUID?: string
  reputationPreference?: any
  toggleEditRenterApplication?: () => void
  isManagerAdded?: boolean
  requireCurrentEmploymentOnly?: boolean
  isQLDForm: boolean
  showEmploymentGrossAnnualIncome?: boolean
}

type EmploymentDetailsComponentProps = {
  employer?: Employer
  applicationId?: string
  agencyGUID?: string
  displayedApplicant?: any
  isQLDForm?: boolean
  showEmploymentGrossAnnualIncome?: boolean
}

export const EmploymentComponent = ({
  currentEmployers = [],
  previousEmployers = [],
  displayedApplicant = {},
  onNewNoteUpdated,
  newNotesContent,
  onNoteIconClicked,
  onNewNoteAdded,
  onNoteCrossClicked,
  interpretNoteResult = {},
  enableNotesButton,
  section,
  applicationId = '',
  id = '',
  toggleRequestInfoModal,
  agencyGUID = '',
  toggleEditRenterApplication,
  isManagerAdded = false,
  requireCurrentEmploymentOnly = false,
  isQLDForm,
  showEmploymentGrossAnnualIncome = true,
}: EmploymentComponentProps) => {
  const {
    preference: { optOutEmploymentHistory },
    preference,
    firstName,
    lastName,
  } = displayedApplicant
  const getCurrentStatus = stringHelpers.currentEmploymentStatus(preference)
  const displayCurrentStatus = getCurrentStatus !== '' && !isQLDForm
  const applicantName = firstName + ' ' + lastName

  const getEmploymentHistoryFilteredLastNotes = (employer: Employer) => {
    const noteResult = JSON.parse(JSON.stringify(interpretNoteResult || {}))
    const allNotes = JSON.parse(
      JSON.stringify(noteResult.employmentDetails?.categorizedAllNotes || {}),
    )
    let filteredCategorizedLastNotes: { [key: number]: Note | undefined } = {}

    if (!Object.keys(noteResult).length) {
      return
    }

    // - split notes over multiple rental histories.
    // - find the latest notes for each action type with targetGUID = employer.guidID
    // - or the latest with empty targetGUID to fall back the old record
    const getLatestTypeRecord = (type: number): Note | undefined => {
      let typeNotes = allNotes[type]?.filter((note: Note) => {
        return note.targetGUID === employer.guidID
      })

      if (!typeNotes.length) {
        typeNotes = allNotes[type]?.filter((note: Note) => {
          return note.targetGUID.length === 0
        })
      }

      if (!typeNotes.length) return undefined

      return (
        typeNotes.reduce((n1: Note, n2: Note) =>
          new Date(n1?.updatedAt) > new Date(n2?.updatedAt) ? n1 : n2,
        ) || undefined
      )
    }

    if (allNotes) {
      filteredCategorizedLastNotes[noteActionType.approve] =
        getLatestTypeRecord(noteActionType.approve)
      filteredCategorizedLastNotes[noteActionType.decline] =
        getLatestTypeRecord(noteActionType.decline)
      filteredCategorizedLastNotes[noteActionType.comment] =
        getLatestTypeRecord(noteActionType.comment)

      // find the latest note among variant action types
      const latestTargetNote = Object.values(filteredCategorizedLastNotes)
        .filter((n) => !!n)
        .reduce(
          (n1: Note | undefined, n2: Note | undefined) =>
            new Date(n1?.updatedAt || 0) > new Date(n2?.updatedAt || 0)
              ? n1
              : n2,
          undefined,
        )

      noteResult.employmentDetails.type = latestTargetNote?.type || 0

      noteResult.employmentDetails.categorizedLastNotes =
        filteredCategorizedLastNotes
    }

    return noteResult
  }

  return (
    <div
      id={id}
      className="container-component bbs pdf-mt0 default-font-family"
    >
      <div className={`container-header position-relative`}>
        <img
          className="standard-icon-spacing"
          src={jobIcon}
          alt="Summary Icon"
        />
        <span className={`standard-subheading`}>Employment Details</span>
      </div>

      <div className="application-employment">
        <div className="application-identification-container pdf-display-flex flex-wrap-wrap pdf-flex-direction-row">
          {displayCurrentStatus && (
            <div className="width-100 mb10">
              <b>{applicantName}</b> is currently: <b>{getCurrentStatus}</b>
            </div>
          )}
          {currentEmployers.length > 0 &&
            currentEmployers.map((employer) => {
              return (
                <Box key={employer.guidID}>
                  <ContainerHeader
                    isEditable={false}
                    onNewNoteUpdated={onNewNoteUpdated}
                    newNotesContent={newNotesContent}
                    onNoteIconClicked={onNoteIconClicked}
                    onNewNoteAdded={onNewNoteAdded}
                    onNoteCrossClicked={onNoteCrossClicked}
                    interpreteNoteResult={getEmploymentHistoryFilteredLastNotes(
                      employer,
                    )}
                    enableNotesButton={enableNotesButton}
                    displayedApplicant={displayedApplicant}
                    section={section}
                    toggleRequestInfoModal={toggleRequestInfoModal || undefined}
                    toggleEditRenterApplication={toggleEditRenterApplication}
                    isManagerAdded={isManagerAdded}
                    targetGUID={employer.guidID}
                    iconHeaderCmp={null}
                  />

                  <EmploymentDetailsComponent
                    employer={employer}
                    applicationId={applicationId}
                    agencyGUID={agencyGUID}
                    displayedApplicant={displayedApplicant}
                    isQLDForm={isQLDForm}
                    showEmploymentGrossAnnualIncome={
                      showEmploymentGrossAnnualIncome
                    }
                  />
                </Box>
              )
            })}
          {previousEmployers.length > 0 &&
            !requireCurrentEmploymentOnly &&
            previousEmployers.map((employer) => {
              return (
                <EmploymentDetailsComponent
                  key={employer.guidID}
                  employer={employer}
                  applicationId={applicationId}
                  agencyGUID={agencyGUID}
                  displayedApplicant={displayedApplicant}
                  isQLDForm={isQLDForm}
                  showEmploymentGrossAnnualIncome={
                    showEmploymentGrossAnnualIncome
                  }
                />
              )
            })}
        </div>
        {previousEmployers.length + currentEmployers.length === 0 &&
          !optOutEmploymentHistory && (
            <div className="no-info-provided">Not provided</div>
          )}
        {previousEmployers.length + currentEmployers.length === 0 &&
          optOutEmploymentHistory && (
            <div className="no-info-provided">
              Does not have employment history
            </div>
          )}
      </div>
    </div>
  )
}

const EmploymentDetailsComponent = ({
  employer,
  applicationId = '',
  agencyGUID = '',
  displayedApplicant = {},
  isQLDForm,
  showEmploymentGrossAnnualIncome = true,
}: EmploymentDetailsComponentProps) => {
  const {
    employerName,
    employerTradingName,
    employmentType,
    employerABN,
    startDate,
    endDate,
    jobTitle,
    grossAnnualIncome,
    stillEmployed,
    verifyDate,
    verifyStatus,
    address,
    employerConfirmation,
    guidID: employmentHistoryId,
    hasAccountant,
    lastConfirmationRequest = {},
    contactName: originalContactName = '',
    email: originalEmail = '',
    phoneNumber: originalPhoneNumber = '',
    confirmedContactName = '',
    confirmedEmail = '',
    confirmedEmploymentType = 0,
    confirmedEndDate = '',
    confirmedGrossAnnualIncome,
    confirmedJobTitle,
    confirmedPhoneNumber,
    confirmedStartDate,
    confirmedWeeklyNetIncome,
    confirmedStillEmployed,
    reasonAffectingEmployment,
    pendingEmployerConfirmationGuidID,
  } = employer || {}
  const displayJobTitle = stringHelpers.capitalizeFirstLetterOfString(
    confirmedJobTitle || jobTitle,
  )
  const displayEmployerName =
    stringHelpers.capitalizeFirstLetterOfString(employerName)
  const employmentText =
    displayJobTitle && displayEmployerName
      ? `${displayJobTitle} at ${displayEmployerName}`
      : displayEmployerName || displayJobTitle
  const duration = calculateDuration(
    confirmedStartDate || startDate,
    confirmedEndDate || endDate,
    confirmedStillEmployed === undefined
      ? stillEmployed
      : confirmedStillEmployed,
  )
  const createdFromQLDForm = !employmentType
  const showAllFields = !isQLDForm && !createdFromQLDForm
  const addressText =
    address?.friendlyName +
    ', ' +
    address?.suburb +
    ', ' +
    address?.state +
    ', ' +
    address?.postcode +
    ', ' +
    address?.country
  const hasAddress =
    Boolean(address?.friendlyName) ||
    Boolean(address?.suburb) ||
    Boolean(address?.postcode)
  const hasDuration = Boolean(duration)
  const hasSummaryMetaRow = showAllFields || hasDuration
  const { comment = '', reason, refereeType = '' } = employerConfirmation || {}
  const type =
    EmploymentTypes[confirmedEmploymentType as keyof typeof EmploymentTypes] ||
    'N/A'
  const isManualReferee =
    refereeTypeCollection[refereeType as number]?.typeName ===
    refereeTypeObject.manualReferee
  const displayContactName = isManualReferee
    ? originalContactName
    : confirmedContactName
  const displayEmail = isManualReferee ? originalEmail : confirmedEmail
  const displayPhoneNumber = isManualReferee
    ? originalPhoneNumber
    : confirmedPhoneNumber
  const formattedPhoneNumber =
    phoneNumberHelpers.formatPhoneNumber(displayPhoneNumber)
  const employmentTitleIcon =
    isEmploymentStatusVerified(verifyStatus) && showAllFields ? greenTick : ''
  const displayGrossAnnualIncome =
    confirmedGrossAnnualIncome ?? grossAnnualIncome ?? 0
  const displayWeeklyNetIncome =
    confirmedWeeklyNetIncome ?? employer?.weeklyNetIncome ?? 0
  const resolvedStillEmployed =
    confirmedStillEmployed === undefined
      ? stillEmployed
      : confirmedStillEmployed
  const resolvedEmploymentEndDate = confirmedEndDate || endDate
  const shouldShowEmploymentEndDate =
    resolvedStillEmployed === false && Boolean(resolvedEmploymentEndDate)
  const employmentEndDateText =
    shouldShowEmploymentEndDate && resolvedEmploymentEndDate
      ? moment(resolvedEmploymentEndDate).format('D MMM YYYY')
      : ''
  const weeklyGrossText = (
    displayGrossAnnualIncome
      ? currencyFormat(displayGrossAnnualIncome / 52, 2)
      : ''
  ) as string

  const jobTitleCompanyTextIcon =
    isEmploymentStatusUnverified(verifyStatus) &&
    (isEmploymentDeclineReasonJobTitle(reason) ||
      isEmploymentDeclineReasonCompanyName(reason))
      ? exclamation
      : employmentTitleIcon
  const tenureTextIcon =
    isEmploymentStatusUnverified(verifyStatus) &&
    isEmploymentDeclineEmploymentDate(reason)
      ? exclamation
      : employmentTitleIcon
  const incomeTextIcon =
    isEmploymentStatusUnverified(verifyStatus) &&
    isEmploymentDeclineReasonSalary(reason)
      ? exclamation
      : employmentTitleIcon

  const isReasonWithEmploymentDetails =
    isEmploymentDeclineReasonJobTitle(reason) ||
    isEmploymentDeclineReasonCompanyName(reason) ||
    isEmploymentDeclineEmploymentDate(reason) ||
    isEmploymentDeclineReasonSalary(reason)

  const onResend = (newEmail: string, message: string) => {
    const payload = {
      overrideRecipient: !!newEmail,
      recipientEmail: newEmail || displayEmail,
      includePersonalMessage: !!message,
      personalMessage: message,
    }
    return resendReferenceRequest(
      employmentHistoryId,
      applicationId,
      payload,
      agencyGUID,
    )
      .then(() => snugNotifier.success('Requested successfully'))
      .catch((error: { message: string }) => {
        snugNotifier.error(error.message)
        return Promise.reject(error)
      })
  }

  const layoutClassName = isQLDForm
    ? 'flex-column-on-mobile'
    : hasSummaryMetaRow
    ? 'application-employment-layout'
    : ''

  return (
    <div className="mb20 pdf-address-history-container">
      <div className="new-manager-empployment-section-container">
        <div className="application-history-header">
          <div className="left-header pdf-font-size-10">
            {employmentText}
            {jobTitleCompanyTextIcon && (
              <img
                className="ml5 mr5 pb2 icon-size"
                src={jobTitleCompanyTextIcon}
                alt="icon"
              />
            )}
          </div>
          {!isSelfEmployedNoAccountant(
            confirmedEmploymentType,
            hasAccountant,
          ) &&
            showAllFields && (
              <div className="right-header">
                <div className="verified-date pdf-font-size-10">
                  {verifyDate
                    ? `  ${moment(verifyDate).format('HH:mm DD MMM YYYY')}`
                    : ''}
                </div>
                <div
                  className={`verified-status ${
                    EmploymentVerifyStatus[verifyStatus as number]
                  } pdf-hide`}
                >
                  {EmploymentVerifyStatus[verifyStatus as number]}
                </div>
              </div>
            )}
        </div>
        <div className="application-seperator" />
        <div
          className={`application-identification-sub-container p0 mt15 pdf-flex-direction-column ${layoutClassName}`}
        >
          {hasSummaryMetaRow && (
            <div className="display-flex">
              {showAllFields && (
                <ApplicationDetailsNumbersComponent
                  title="Type"
                  text={type}
                  componentClassName={'justify-content-unset'}
                  componentMinWidth={'min-width120p'}
                  textClassName="pdf-font-size-10"
                  titleTextClassName="pdf-font-size-10"
                />
              )}
              {hasDuration && (
                <ApplicationDetailsNumbersComponent
                  icon={tenureTextIcon}
                  title="Tenure"
                  text={duration}
                  componentClassName={'justify-content-unset flex-2'}
                  componentMinWidth={'min-width120p'}
                  textClassName="pdf-font-size-10"
                  titleTextClassName="pdf-font-size-10"
                />
              )}
            </div>
          )}
          <Flex ml={0}>
            {showEmploymentGrossAnnualIncome && (
              <ApplicationDetailsNumbersComponent
                icon={incomeTextIcon}
                title="Gross income (Submitted)"
                text={`$${displayGrossAnnualIncome}`}
                extraText={'p/a'}
                titleTextClassName={'employment-title-text pdf-font-size-10'}
                componentClassName={'justify-content-unset'}
                componentMinWidth={'min-width120p'}
                textClassName="pdf-font-size-10"
              />
            )}
            {showAllFields || !showEmploymentGrossAnnualIncome ? (
              <ApplicationDetailsNumbersComponent
                icon={incomeTextIcon}
                title="After tax (Submitted)"
                text={`$${displayWeeklyNetIncome}`}
                extraText={'p/w'}
                titleTextClassName={'employment-title-text pdf-font-size-10'}
                componentClassName={'justify-content-unset'}
                componentMinWidth={'min-width120p'}
                textClassName="pdf-font-size-10"
              />
            ) : (
              <ApplicationDetailsNumbersComponent
                title="Weekly Gross (Submitted)"
                text={weeklyGrossText}
                extraText="p/w"
                titleTextClassName={'employment-title-text pdf-font-size-10'}
                componentClassName={'justify-content-unset'}
                componentMinWidth={'min-width120p'}
                textClassName="pdf-font-size-10"
              />
            )}
          </Flex>
        </div>
        {hasAddress && (
          <div>
            <ApplicationDetailsNumbersComponent
              title="Address"
              text={addressText}
              componentClassName={'justify-content-unset'}
              textClassName="pdf-font-size-10"
              titleTextClassName="pdf-font-size-10"
            />
          </div>
        )}
        {shouldShowEmploymentEndDate && (
          <div>
            <ApplicationDetailsNumbersComponent
              title="Most recent employment end date"
              text={employmentEndDateText}
              componentClassName={'justify-content-unset'}
              textClassName="pdf-font-size-10"
              titleTextClassName="pdf-font-size-10"
            />
          </div>
        )}
        {showAllFields && (
          <>
            {isManualReferee && (
              <div>
                <ApplicationDetailsNumbersComponent
                  title="Employment Verification"
                  text={`Manually verified${
                    verifyDate
                      ? ` on ${moment(verifyDate).format('LT Do, MMM')}`
                      : ''
                  }`}
                  componentClassName={'justify-content-unset'}
                  textClassName="pdf-font-size-10"
                  titleTextClassName="pdf-font-size-10"
                />
              </div>
            )}

            {reasonAffectingEmployment && (
              <div>
                <ApplicationDetailsNumbersComponent
                  title="Is there any work issue that might affect the applicant's ongoing employment, income or ability to pay rent?"
                  text={reasonAffectingEmployment}
                  componentClassName={'justify-content-unset'}
                  textClassName="pdf-font-size-10"
                  titleTextClassName="pdf-font-size-10"
                />
              </div>
            )}
            {isSelfEmployed(confirmedEmploymentType) && (
              <>
                <div className="application-detials-numbers-component height-auto">
                  <div className="title mb5">
                    <div className="pdf-font-size-10">Company details</div>
                  </div>
                </div>
                <div className="fw500 mb5">
                  {employerName ? `Name: ${employerName}` : ''}
                </div>
                <div className="fw500 mb5">
                  {employerTradingName
                    ? `Trading name: ${employerTradingName}`
                    : ''}
                </div>
                <div className="fw500 mb10">
                  {employerABN
                    ? `${getBusinessNumberLabel(address)}: ${employerABN}`
                    : ''}
                </div>
              </>
            )}

            <div className="application-detials-numbers-component height-auto">
              <div className="title mb5">
                <div className="pdf-font-size-10">
                  {isSelfEmployed(confirmedEmploymentType)
                    ? 'Accountant'
                    : isManualReferee
                    ? 'Contact (manually verified)'
                    : 'Contact'}
                </div>
              </div>
            </div>
            {isSelfEmployedNoAccountant(
              confirmedEmploymentType,
              hasAccountant,
            ) ? (
              <div>No accountant details</div>
            ) : (
              <div className="applicant-container">
                <div className="applicant-upper-container display-flex">
                  <div className="household-applicant-table avatar-container mt0">
                    <div className="avatar application-avatar">
                      <span className="app-avatar-initial-position default-font-family">
                        {displayContactName !== ''
                          ? capitalizeFirstLetter(
                              displayContactName.substring(0, 1),
                            )
                          : ''}
                      </span>
                    </div>
                  </div>
                  <Display.StandardContactInfoContainer
                    mainTitle={
                      displayContactName !== ''
                        ? capitalizeFirstLetter(displayContactName)
                        : ''
                    }
                    firstLineText={formattedPhoneNumber}
                    secondLineText={displayEmail}
                    statusIcon={isManualReferee ? undefined : tenureTextIcon}
                    badgeCmp={null}
                  />
                </div>
              </div>
            )}

            {!employerConfirmation?.guidID && (
              <Display.ResendReference
                from="renterSummary"
                onSubmitResend={onResend}
                lastSend={{
                  email: lastConfirmationRequest?.requestReceiver,
                  senderEmail: lastConfirmationRequest?.requestSender,
                  sentAt: lastConfirmationRequest?.requestSentTime,
                }}
                manualReferenceLink={`${urlTo(
                  'addManualEmploymentVerification',
                  {
                    employmentConfirmationId: pendingEmployerConfirmationGuidID,
                  },
                )}?applicationId=${applicationId}&applicantId=${
                  displayedApplicant.guidID
                }&manual=true`}
                enableManualReference={
                  Features.FeatureFlag.ManualRentalReference.isOn
                }
                isResponded={false}
                changeReferenceCmp={null}
                lastRequest={null}
                isDeclined={false}
                expiresAt={null}
              />
            )}
            {!!isEmploymentStatusUnverified(verifyStatus) && (
              <div>
                <div className="application-seperator" />
                <div className="application-employment-verification-container">
                  <div className="title">Verification</div>
                  <div className="employer-comment-container">
                    {comment ? comment : 'No information provided'}
                  </div>
                  {!!isReasonWithEmploymentDetails && (
                    <ReferenceResultComponent
                      description={`Confirmed ${deCapitalizeFirstLetter(
                        EmploymentDeclineReasons[reason as number],
                      )}`}
                      isBooleanRating
                      exception
                      boolRatingValue="No"
                    />
                  )}
                  {!!isEmploymentDeclineContactPerson(reason) && (
                    <ReferenceResultComponent
                      description={`I am the correct ${deCapitalizeFirstLetter(
                        EmploymentDeclineReasons[reason as number],
                      )}`}
                      isBooleanRating
                      exception
                      boolRatingValue="No"
                    />
                  )}
                  {!!isEmploymentDeclineOther(reason) && (
                    <ReferenceResultComponent
                      description={EmploymentDeclineReasons[reason as number]}
                      isBooleanRating
                      exception
                      boolRatingValue="Yes"
                    />
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
